import { useState, useRef, useEffect } from 'react';
import { toast } from 'sonner';
import { _normProblems, _normMeds, _normAllergies, _normVitals, _deduped, API } from '../../components/lumina/doctor/doctorModeData';
import { useAuth } from '../../contexts/AuthContext';

const _DBG = process.env.NODE_ENV === 'development';

const usePatientChartData = ({ activeApt, patient, setPatient }) => {
  const { authFetch } = useAuth();
  const [absorbedData, setAbsorbedData] = useState(null);
  const [chartUploads, setChartUploads] = useState([]);
  const [patientDocuments, setPatientDocuments] = useState([]);
  const chartFileRef = useRef(null);
  const chartPollTimers = useRef({});

  // Cleanup poll timers on unmount
  useEffect(() => {
    const timers = chartPollTimers.current;
    return () => { Object.values(timers).forEach(clearTimeout); };
  }, []);

  // Fetch absorbed chart data when patient changes
  useEffect(() => {
    if (!patient) { setAbsorbedData(null); return; }
    let cancelled = false;
    const pid = activeApt?.patientId || patient?.mrn || patient?.id;
    if (!pid) return;
    (async () => {
      try {
        if (_DBG) console.info('[chart-data fetch] pid=%s', pid);
        const res = await authFetch(`${API}/api/lumina/patient/${pid}/chart-data`);
        if (res.ok && !cancelled) {
          const data = await res.json();
          if (_DBG) console.info('[chart-data result] pid=%s server_pid=%s items=%d meds=%d problems=%d allergies=%d',
            pid, data.patient_id, data.absorption_item_count,
            (data.sections?.medications || []).length,
            (data.sections?.problems || []).length,
            (data.sections?.allergies || []).length);

          // Patient consistency check
          if (data.patient_id && data.patient_id !== pid) {
            if (_DBG) console.warn('[chart-data] patient_id mismatch: queried=%s returned=%s', pid, data.patient_id);
          }

          if (data.absorption_item_count > 0) {
            setAbsorbedData(data);
            // Merge absorbed sections into patient
            const sec = data.sections || {};
            setPatient(prev => {
              if (!prev) return prev;
              const updated = {
                ...prev,
                problems: _deduped(prev.problems, _normProblems(sec.problems), p => (p.icd || p.name || '').toLowerCase()),
                medications: _deduped(prev.medications, _normMeds(sec.medications), m => (m.name || '').toLowerCase()),
                allergies: _deduped(prev.allergies, _normAllergies(sec.allergies), a => (a.allergen || '').toLowerCase()),
                vitals: { ...(prev.vitals || {}), ..._normVitals(sec.vitals) },
              };
              if (_DBG) console.info('[chart-data merge] meds: %d -> %d, problems: %d -> %d, allergies: %d -> %d',
                (prev.medications || []).length, updated.medications.length,
                (prev.problems || []).length, updated.problems.length,
                (prev.allergies || []).length, updated.allergies.length);
              return updated;
            });
          } else {
            setAbsorbedData(data);
          }
        }
      } catch { /* endpoint unavailable — static data fallback */ }
    })();
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patient?.mrn, activeApt?.patientId]);

  // Fetch real patient documents from backend
  useEffect(() => {
    const pid = activeApt?.patientId || patient?.mrn || patient?.id;
    if (!pid) { setPatientDocuments([]); return; }
    let cancelled = false;
    (async () => {
      try {
        const res = await authFetch(`${API}/api/patient-documents/patient/${pid}/documents`);
        if (res.ok && !cancelled) {
          const data = await res.json();
          setPatientDocuments((data.records || []).map(r => ({
            id: r.id,
            name: r.filename || r.file_name || 'Document',
            type: r.document_type || r.content_type || 'Upload',
            date: r.timestamp ? new Date(r.timestamp).toLocaleDateString() : '',
            status: r.status,
            analysis_id: r.analysis_id,
          })));
        }
      } catch { setPatientDocuments([]); }
    })();
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patient?.mrn, activeApt?.patientId]);

  /* ── Chart Document Upload handlers ── */
  const handleChartUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = '';
    const patientId = activeApt?.patientId || patient?.id;
    const entry = { id: `cu-${Date.now()}`, name: file.name, status: 'uploading', uploadedAt: new Date().toISOString(), analysisId: null, absorbed: false };
    setChartUploads(prev => [entry, ...prev]);

    try {
      const form = new FormData();
      form.append('file', file);
      const url = patientId ? `${API}/api/patient-documents/upload?patient_id=${patientId}` : `${API}/api/patient-documents/upload`;
      const res = await authFetch(url, { method: 'POST', body: form });
      if (!res.ok) throw new Error(`Upload failed (${res.status})`);
      const data = await res.json();
      const aid = data.analysis_id || data.id;
      setChartUploads(prev => prev.map(u => u.id === entry.id ? { ...u, status: 'processing', analysisId: aid } : u));
      toast.success(`"${file.name}" uploaded — processing`);
      pollChartUpload(entry.id, aid);
    } catch (err) {
      setChartUploads(prev => prev.map(u => u.id === entry.id ? { ...u, status: 'failed' } : u));
      toast.error(err.message || 'Upload failed');
    }
  };

  const refreshPatientDocuments = () => {
    const pid = activeApt?.patientId || patient?.mrn || patient?.id;
    if (!pid) return;
    authFetch(`${API}/api/patient-documents/patient/${pid}/documents`)
      .then(r => r.ok ? r.json() : null)
      .then(data => {
        if (data?.records) {
          setPatientDocuments(data.records.map(r => ({
            id: r.id,
            name: r.filename || r.file_name || 'Document',
            type: r.document_type || r.content_type || 'Upload',
            date: r.timestamp ? new Date(r.timestamp).toLocaleDateString() : '',
            status: r.status,
            analysis_id: r.analysis_id,
            extracted_data: r.extracted_data,
          })));
        }
      })
      .catch(() => {});
  };

  const autoExtractAndRefresh = async (entryId) => {
    const pid = activeApt?.patientId || patient?.mrn || patient?.id;
    if (!pid) return;
    try {
      // Find the matching doc record to get its record_id for extraction
      const histRes = await authFetch(`${API}/api/patient-documents/patient/${pid}/documents`);
      if (!histRes.ok) return;
      const hist = await histRes.json();
      const entry = chartUploads.find(u => u.id === entryId);
      const matchingDoc = (hist.records || []).find(r => r.analysis_id === entry?.analysisId);
      if (matchingDoc?.id) {
        const extractRes = await authFetch(`${API}/api/patient-documents/history/${matchingDoc.id}/extract?patient_id=${encodeURIComponent(pid)}`, { method: 'POST' });
        if (extractRes.ok) {
          setChartUploads(prev => prev.map(u => u.id === entryId ? { ...u, status: 'extracted' } : u));
          // Refresh chart data so extracted vitals/problems/meds appear immediately
          try {
            const chartRes = await authFetch(`${API}/api/lumina/patient/${pid}/chart-data`);
            if (chartRes.ok) {
              const data = await chartRes.json();
              if (data.absorption_item_count > 0) {
                setAbsorbedData(data);
                const sec = data.sections || {};
                setPatient(prev => {
                  if (!prev) return prev;
                  return {
                    ...prev,
                    problems: _deduped(prev.problems, _normProblems(sec.problems), p => (p.icd || p.name || '').toLowerCase()),
                    medications: _deduped(prev.medications, _normMeds(sec.medications), m => (m.name || '').toLowerCase()),
                    allergies: _deduped(prev.allergies, _normAllergies(sec.allergies), a => (a.allergen || '').toLowerCase()),
                    vitals: { ...(prev.vitals || {}), ..._normVitals(sec.vitals) },
                  };
                });
              }
            }
          } catch { /* non-fatal */ }
        } else {
          setChartUploads(prev => prev.map(u => u.id === entryId ? { ...u, status: 'completed' } : u));
        }
      } else {
        setChartUploads(prev => prev.map(u => u.id === entryId ? { ...u, status: 'completed' } : u));
      }
    } catch {
      setChartUploads(prev => prev.map(u => u.id === entryId ? { ...u, status: 'completed' } : u));
    }
    refreshPatientDocuments();
  };

  const pollChartUpload = (entryId, analysisId) => {
    if (!analysisId) return;
    let attempts = 0;
    const poll = async () => {
      attempts++;
      try {
        const res = await authFetch(`${API}/api/analyses/${analysisId}`);
        if (!res.ok) throw new Error();
        const data = await res.json();
        const s = data.status?.toLowerCase();
        if (s === 'completed' || s === 'done') {
          setChartUploads(prev => prev.map(u => u.id === entryId ? { ...u, status: 'extracting', result: data } : u));
          // Auto-extract clinical data then refresh
          autoExtractAndRefresh(entryId);
          return;
        }
        if (s === 'failed' || s === 'error') {
          setChartUploads(prev => prev.map(u => u.id === entryId ? { ...u, status: 'failed' } : u));
          refreshPatientDocuments();
          return;
        }
      } catch { /* keep polling */ }
      if (attempts < 30) chartPollTimers.current[entryId] = setTimeout(poll, 2000);
      else setChartUploads(prev => prev.map(u => u.id === entryId ? { ...u, status: 'completed' } : u));
    };
    poll();
  };

  const handleChartAbsorb = async (entry) => {
    if (!entry.analysisId) return;
    const patientId = activeApt?.patientId || patient?.mrn || patient?.id;
    try {
      const res = await authFetch(`${API}/api/analyses/absorb-to-chart`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ analysis_id: entry.analysisId, patient_id: patientId }),
      });
      if (!res.ok) throw new Error(`Absorb failed (${res.status})`);
      setChartUploads(prev => prev.map(u => u.id === entry.id ? { ...u, absorbed: true } : u));
      toast.success(`"${entry.name}" absorbed to chart`);
      // Refresh chart data from correct endpoint
      if (patientId) {
        try {
          const chartRes = await authFetch(`${API}/api/lumina/patient/${patientId}/chart-data`);
          if (chartRes.ok) {
            const data = await chartRes.json();
            if (_DBG) console.info('[chart-data refresh] patient=%s items=%d meds=%d',
              patientId, data.absorption_item_count, (data.sections?.medications || []).length);
            if (data.absorption_item_count > 0) {
              setAbsorbedData(data);
              const sec = data.sections || {};
              setPatient(prev => {
                if (!prev) return prev;
                return {
                  ...prev,
                  problems: _deduped(prev.problems, _normProblems(sec.problems), p => (p.icd || p.name || '').toLowerCase()),
                  medications: _deduped(prev.medications, _normMeds(sec.medications), m => (m.name || '').toLowerCase()),
                  allergies: _deduped(prev.allergies, _normAllergies(sec.allergies), a => (a.allergen || '').toLowerCase()),
                  vitals: { ...(prev.vitals || {}), ..._normVitals(sec.vitals) },
                };
              });
            } else {
              setAbsorbedData(data);
            }
          }
        } catch { /* non-fatal */ }
      }
    } catch (err) {
      toast.error(err.message || 'Absorb failed');
    }
  };

  return {
    absorbedData,
    setAbsorbedData,
    chartUploads,
    chartFileRef,
    handleChartUpload,
    handleChartAbsorb,
    patientDocuments,
  };
};

export default usePatientChartData;
